import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import apiClient from "../../apiClient";
import { crmDataService } from "../../pages/HR/CRM/utils/crmDataService";
import { useImpersonation } from "../../context/ImpersonationContext";
import { ANALYTICS_OWNER_EMAILS, pages } from "../../utils/page-permissions";
import logo from "../logo.png";
import NavDropdown from "./NavDropdown";
import NavItem from "./NavItem";
import MobileMenu from "./MobileMenu";
import ViewAsDropdown from "./ViewAsDropdown";
import ThemeToggle from "../common/ThemeToggle";
import './Header.css';

const APPROVAL_POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes

// The Settings link follows the /settings route's `allowed` list in
// page-permissions.js, the source of truth. A hand-kept list here showed the
// link to ta, ld and ld_director, whom the route then silently bounced.
const SETTINGS_ROLES = pages.find((page) => page.path === "/settings")?.allowed || [];

function Header() {
  const location = useLocation();
  const { instance } = useMsal();
  const headerRef = useRef(null);
  const isLoginPage = location.pathname === "/";

  // Check if user is authenticated
  const isAuthenticated = () => {
    const token = localStorage.getItem("authToken");
    const role = localStorage.getItem("role") || localStorage.getItem("userRole");
    return !!(token && role);
  };

  const [permissions, setPermissions] = useState({
    canAccessDashboard: false,
    canAccessPeoplePlan: false,
    canAccessHR: false,
    canAccessTA: false,
    canAccessLD: false,
    canAccessLOA: false,
    canAccessFLM: false,
    canAccessFLMTrackers: false,
    canAccessSettings: false,
    canAccessAnalytics: false,
    canAccessHRStaff: false,
    canAccessTAStaff: false,
    canAccessRM: false,
    canAccessRMStaff: false,
    canAccessLDStaff: false,
    canAccessIT: false,
    canAccessITStaff: false,
    isApprover: false,
  });

  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  const approvalIntervalRef = useRef(null);

  // Impersonation context
  const { effectiveRole, isActive: isImpersonating, exitImpersonation, userEmail: impersonatedEmail } = useImpersonation();

  // Only lbarbosa can see the View As dropdown
  const canSeeViewAs = () => {
    return userEmail?.toLowerCase() === "lbarbosa@cafuamanagement.com";
  };

  // The environment switch is hard-disabled below (showEnvSwitch={false}), so the
  // helpers that computed its visibility, URL and label were dead code. Removed —
  // recover them from git history if the switch is ever turned back on.

  // Fetch pending approval count for nav badge
  const fetchPendingApprovalCount = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      // Use impersonated email when active, otherwise localStorage
      const effectiveEmail = (isImpersonating && impersonatedEmail)
        ? impersonatedEmail
        : localStorage.getItem("userEmail");
      if (!effectiveEmail) return;

      const response = await apiClient.get('/crm/approvals/pending');
      const allPending = response.data?.data || [];
      const myPending = allPending.filter(
        (a) => a.approver_email?.toLowerCase() === effectiveEmail.toLowerCase()
      );
      setPendingApprovalCount(myPending.length);
    } catch {
      // Silently ignore — badge just won't show
    }
  }, [isImpersonating, impersonatedEmail]);

  // Poll for pending approvals and refresh on navigation
  useEffect(() => {
    if (isLoginPage || !isAuthenticated()) return;

    fetchPendingApprovalCount();

    approvalIntervalRef.current = setInterval(fetchPendingApprovalCount, APPROVAL_POLL_INTERVAL);
    return () => {
      if (approvalIntervalRef.current) clearInterval(approvalIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoginPage, fetchPendingApprovalCount]);

  // Refresh count when navigating to/from My Approvals
  useEffect(() => {
    if (location.pathname.startsWith('/hr/crm/approvals') || location.pathname.startsWith('/rm/crm/approvals') || location.pathname.startsWith('/ld/crm/approvals') || location.pathname.startsWith('/it/crm/approvals')) {
      fetchPendingApprovalCount();
    }
  }, [location.pathname, fetchPendingApprovalCount]);

  // Navigation structure configuration
  const navigationConfig = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      path: '/dashboard',
      type: 'link',
      requiresPermission: 'canAccessDashboard',
    },
    {
      id: 'peopleplan',
      label: 'People Plan',
      path: '/peopleplan',
      type: 'link',
      requiresPermission: 'canAccessPeoplePlan',
    },
    {
      id: 'hr',
      label: 'HR',
      path: '/hr',
      type: 'dropdown',
      requiresPermission: 'canAccessHR',
      items: [
        { label: 'HR Dashboard', path: '/hr', requiresPermission: 'canAccessHRStaff' },
        { label: 'My Approvals', path: '/hr/crm/approvals', badge: pendingApprovalCount, requiresPermission: 'isApprover' },
        { label: 'HR Requests', path: '/hr/crm/tickets' },
        { label: 'To Do Tickets', path: '/hr/crm/todo-tickets', requiresPermission: 'canAccessHRStaff' },
        { label: 'New Hire Tracker', path: '/hr/new-hire-tracker', requiresPermission: 'canAccessHRStaff' },
        { type: 'separator', label: 'Leave of Absence', requiresPermission: 'canAccessLOA' },
        { label: 'LOA Dashboard', path: '/loa', requiresPermission: 'canAccessLOA' },
        { label: 'Cases', path: '/loa/cases', requiresPermission: 'canAccessLOA' },
        { label: 'Calendar', path: '/loa/calendar', requiresPermission: 'canAccessLOA' },
        { label: 'Reports', path: '/loa/reports', requiresPermission: 'canAccessLOA' },
      ]
    },
    {
      id: 'ta',
      label: 'TA',
      path: '/ta',
      type: 'dropdown',
      requiresPermission: 'canAccessTA',
      items: [
        { label: 'TA Dashboard', path: '/ta', requiresPermission: 'canAccessTAStaff' },
        // Disabled for Phase 1 rebuild - will be re-enabled in Phase 2
        // { label: 'Executive Dashboard', path: '/recruiting/executive', requiresPermission: 'canAccessTAStaff' },
        // { label: 'Pipeline Dashboard', path: '/recruiting/pipeline', requiresPermission: 'canAccessTAStaff' },
        { label: 'Hires Dashboard', path: '/recruiting/hires', requiresPermission: 'canAccessTAStaff' },
        { label: 'TA Requests', path: '/ta/crm/tickets' },
        { label: 'To Do Tickets', path: '/ta/requests', requiresPermission: 'canAccessTAStaff' },
        { label: 'Job Tracker', path: '/ta/jobs', requiresPermission: 'canAccessTAStaff' },
        { label: 'Candidate Tracker', path: '/ta/candidates', requiresPermission: 'canAccessTAStaff' },
        { label: 'Onboarding', path: '/ta/newhires', requiresPermission: 'canAccessTAStaff' },
      ]
    },
    {
      id: 'ld',
      label: 'L&D',
      path: '/ld',
      type: 'dropdown',
      requiresPermission: 'canAccessLD',
      items: [
        { label: 'L&D Dashboard', path: '/ld' },
        { label: 'L&D Requests', path: '/ld/crm/tickets', requiresPermission: 'canAccessLDStaff' },
        { label: 'To Do Tickets', path: '/ld/crm/todo-tickets', requiresPermission: 'canAccessLDStaff' },
        { label: 'My Approvals', path: '/ld/crm/approvals', requiresPermission: 'canAccessLDStaff' },
        { label: 'MIT Tracker', path: '/ld/mit-tracker', requiresPermission: 'canAccessFLMTrackers' },
        { label: 'Certified Training', path: '/ld/certified-training', requiresPermission: 'canAccessFLMTrackers' },
        { label: 'Business Support Plan', path: '/ld/business-support-plan', requiresPermission: 'canAccessFLMTrackers' },
        { label: 'MIT Proficiency Checklist', path: '/ld/mit-proficiency-checklist', requiresPermission: 'canAccessFLMTrackers' },
        { label: 'Depository Files', path: '/ld/files', requiresPermission: 'canAccessFLM' },
        { label: 'Window Readiness Results', path: '/ld/window-readiness', requiresPermission: 'canAccessFLM' },
        { label: 'Center vs Workpulse Comparison', path: '/ld/comparison', requiresPermission: 'canAccessFLM' },
        // Hidden for now - will be enabled in the future
        // { label: 'Goals & OKRs', path: '/peoplepulse/goals' },
        // { label: '1-on-1 Meetings', path: '/peoplepulse/one-on-one' },
        // { label: 'Performance Reviews', path: '/peoplepulse/reviews' },
        // { label: 'Feedback & Recognition', path: '/peoplepulse/feedback' },
        // { label: 'Tasks & Action Items', path: '/peoplepulse/tasks' },
      ]
    },
    {
      id: 'rm',
      label: 'Risk Mgmt',
      path: '/rm',
      type: 'dropdown',
      requiresPermission: 'canAccessRM',
      items: [
        { label: 'RM Requests', path: '/rm/crm/tickets' },
        { label: 'To Do Tickets', path: '/rm/crm/todo-tickets', requiresPermission: 'canAccessRMStaff' },
        { label: 'My Approvals', path: '/rm/crm/approvals', requiresPermission: 'isApprover' },
      ]
    },
    {
      id: 'it',
      label: 'IT',
      path: '/it',
      type: 'dropdown',
      requiresPermission: 'canAccessIT',
      items: [
        { label: 'IT Requests', path: '/it/crm/tickets' },
        { label: 'To Do Tickets', path: '/it/crm/todo-tickets', requiresPermission: 'canAccessITStaff' },
        { label: 'My Approvals', path: '/it/crm/approvals', requiresPermission: 'isApprover' },
      ]
    },
    {
      id: 'analytics',
      label: 'Analytics',
      type: 'dropdown',
      requiresPermission: 'canAccessAnalytics',
      items: [
        { label: 'Analytics Dashboard', path: '/admin/analytics', requiresPermission: 'canAccessAnalytics' },
        { label: 'Operations Monitor', path: '/admin/operations', requiresPermission: 'canAccessAnalytics' },
        { label: 'Error Tracking', path: '/admin/error-tracking', requiresPermission: 'canAccessAnalytics' },
        { label: 'TA Operations', path: '/admin/ta-operations', requiresPermission: 'canAccessAnalytics' },
        { label: 'Email Health', path: '/admin/email-health', requiresPermission: 'canAccessAnalytics' },
        { label: 'Scheduler Health', path: '/admin/scheduler-health', requiresPermission: 'canAccessAnalytics' },
      ]
    },
    {
      id: 'settings',
      label: 'Settings',
      path: '/settings',
      type: 'link',
      requiresPermission: 'canAccessSettings',
    },
  ];

  // Check permissions
  useEffect(() => {
    const checkPermissions = async () => {
      if (isLoginPage) return;

      try {
        const token = localStorage.getItem("authToken");
        const userRole = localStorage.getItem("userRole");
        const storedEmail = localStorage.getItem("userEmail");

        if (!token) {
          setPermissions({
            canAccessDashboard: false,
            canAccessPeoplePlan: false,
            canAccessHR: false,
            canAccessTA: false,
            canAccessLD: false,
            canAccessLOA: false,
            canAccessFLM: false,
            canAccessFLMTrackers: false,
            canAccessSettings: false,
            canAccessAnalytics: false,
            canAccessHRStaff: false,
            canAccessTAStaff: false,
            canAccessRM: false,
            canAccessRMStaff: false,
            canAccessLDStaff: false,
            canAccessIT: false,
            canAccessITStaff: false,
            isApprover: false,
          });
          return;
        }

        // Set user email for environment switch button
        setUserEmail(storedEmail);

        // Use impersonated role when active, otherwise localStorage role
        const normalizedRole = (effectiveRole || userRole)?.toLowerCase();
        const isImpersonatingRole = !!effectiveRole;

        // Check FLM Upload access using apiClient
        // Skip API/email checks when impersonating — only use role-based checks
        let canAccessFLM = false;
        if (!isImpersonatingRole) {
          try {
            const response = await apiClient.get('/api/flm/upload-history');
            canAccessFLM = response.status === 200;
          } catch (err) {
            // API call failed - user doesn't have FLM access
            canAccessFLM = false;
          }
        } else {
          // When impersonating, the live probe would reflect the impersonator,
          // not the target role — so fall back to a role check that mirrors the
          // backend gate canAccessFLMUpload (backend/utils/permissions.js). This
          // makes the FLM nav items (Depository Files, Window Readiness,
          // Comparison) render truthfully under "View As" for ld/ld_director.
          const flmUploadRoles = ["admin", "vp", "director", "ld", "ld_director"];
          canAccessFLM = flmUploadRoles.includes(normalizedRole);
        }

        // FLM Trackers access
        const allowedEmails = [
          "lbarbosa@cafuamanagement.com",
          "adiaz@cafuamanagement.com",
          "cbrown@cafuamanagement.com",
          "cleathers@cafuamanagement.com",
          "jwolkens@cafuamanagement.com",
          "nlarochelle@cafuamanagement.com",
          "smoreschi@cafuamanagement.com",
          "kkeys@cafuamanagement.com",
          "lmarques@cafuamanagement.com",
          "fgcafua@cafuamanagement.com",
          "jfrancisco@cafuamanagement.com",
        ];

        const ldRoles = ["ld", "ld_director", "admin"];
        const hasRoleAccess = ldRoles.includes(normalizedRole);
        // Skip email-based overrides when impersonating — respect the impersonated role only
        const hasEmailAccess = !isImpersonatingRole && allowedEmails.includes(userEmail?.toLowerCase());
        const canAccessFLMTrackers = hasRoleAccess || hasEmailAccess;

        // Department access based on roles (using lowercase role names)
        // HR and TA menus visible to all authenticated users (matches page-permissions.js)
        const isAuthenticated = !!normalizedRole;
        const dashboardRoles = ["admin", "director", "vp", "mum", "rm", "ld_director", "hr", "hr_director", "cb"];
        const peoplePlanRoles = ["admin", "director", "vp", "mum", "rm", "hr", "hr_director", "cb", "ld_director"]; // excludes ta, ld
        const hrStaffRoles = ["hr", "hr_director", "cb", "admin"]; // HR staff only (not mum/rm)
        const taStaffRoles = ["ta", "admin"]; // TA staff only (not hr/mum/rm)
        // rmRoles no longer used — RM menu visible to all authenticated users (like HR/TA)
        const rmStaffRoles = ["rma", "hr", "hr_director", "cb", "admin"]; // RM staff (To Do Tickets)
        const ldRolesAccess = ["ld", "ld_director", "admin"];
        const ldStaffRoles = ["ld", "ld_director", "admin"]; // L&D staff (To Do Tickets, Requests, Approvals)
        // itRoles no longer used — IT menu visible to all authenticated users (like HR/TA/RM)
        const itStaffRoles = ["it", "admin"]; // IT staff (To Do Tickets)
        const loaRoles = ["hr", "hr_director", "cb", "admin"];
        const analyticsOwnerEmails = ANALYTICS_OWNER_EMAILS;

        // Check if user is an approver on any ticket (admin always sees My Approvals)
        let isApprover = normalizedRole === "admin";
        if (!isApprover) {
          try {
            isApprover = await crmDataService.checkIsApprover();
          } catch {
            isApprover = false;
          }
        }

        setPermissions({
          canAccessDashboard: dashboardRoles.includes(normalizedRole),
          canAccessPeoplePlan: peoplePlanRoles.includes(normalizedRole),
          canAccessHR: isAuthenticated,
          canAccessTA: isAuthenticated,
          canAccessHRStaff: hrStaffRoles.includes(normalizedRole),
          canAccessTAStaff: taStaffRoles.includes(normalizedRole),
          canAccessRM: isAuthenticated,
          canAccessRMStaff: rmStaffRoles.includes(normalizedRole),
          canAccessLDStaff: ldStaffRoles.includes(normalizedRole),
          canAccessIT: isAuthenticated,
          canAccessITStaff: itStaffRoles.includes(normalizedRole),
          canAccessLD: ldRolesAccess.includes(normalizedRole) || canAccessFLM || canAccessFLMTrackers,
          canAccessLOA: loaRoles.includes(normalizedRole),
          canAccessFLM,
          canAccessFLMTrackers,
          canAccessSettings: SETTINGS_ROLES.includes(normalizedRole),
          canAccessAnalytics: !isImpersonatingRole && analyticsOwnerEmails.includes(storedEmail?.toLowerCase()),
          isApprover,
        });

      } catch (error) {
        console.error("Error checking permissions:", error);
        setPermissions({
          canAccessDashboard: false,
          canAccessPeoplePlan: false,
          canAccessHR: false,
          canAccessTA: false,
          canAccessLD: false,
          canAccessLOA: false,
          canAccessFLM: false,
          canAccessFLMTrackers: false,
          canAccessSettings: false,
          canAccessAnalytics: false,
          canAccessHRStaff: false,
          canAccessTAStaff: false,
          canAccessLDStaff: false,
          canAccessIT: false,
          canAccessITStaff: false,
          isApprover: false,
        });
      }
    };

    checkPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoginPage, location.pathname, effectiveRole]);

  // Handle logout
  const handleLogout = async () => {
    // Clear impersonation state before logout
    if (isImpersonating) exitImpersonation();

    // Clear all local storage auth items via centralized utility
    const { clearAuthState } = await import("../../utils/auth");
    clearAuthState();

    // Sign out from MSAL to prevent auto-login on return to login page
    try {
      await instance.logoutPopup({
        postLogoutRedirectUri: window.location.origin,
        mainWindowRedirectUri: "/"
      });
    } catch (error) {
      // If popup fails, try redirect logout
      console.error("Popup logout failed, trying redirect:", error);
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin
      });
    }
  };

  // Handle dropdown interactions
  const handleDropdownHover = (dropdownId) => {
    if (window.innerWidth > 768) { // Only on desktop
      setActiveDropdown(dropdownId);
    }
  };

  const handleDropdownClick = (dropdownId) => {
    if (window.innerWidth <= 768) { // Only on mobile
      setActiveDropdown(activeDropdown === dropdownId ? null : dropdownId);
    }
  };

  const handleDropdownLeave = () => {
    if (window.innerWidth > 768) {
      setActiveDropdown(null);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if nav item should be shown based on permissions
  const shouldShowNavItem = (item) => {
    if (!item.requiresPermission) return true;
    return permissions[item.requiresPermission];
  };

  // Show minimal header (logo only) for login page or unauthenticated users
  if (isLoginPage || !isAuthenticated()) {
    return (
      <header className="header">
        <div className="header-container">
          <div className="logo-title-wrapper">
            <div className="logo-title-container">
              <img src={logo} alt="Cafua Management Company Logo" className="logo" />
              <h1 className="header-title">
                <span className="text-pink">People Plan </span>
                <span className="text-orange">App</span>
              </h1>
            </div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="header" ref={headerRef}>
      <div className="header-container">
        <div className="logo-title-wrapper">
          <div className="logo-title-container">
            <img src={logo} alt="Cafua Management Company Logo" className="logo" />
            <h1 className="header-title">
              <span className="text-pink">People Plan </span>
              <span className="text-orange">App</span>
            </h1>
          </div>
        </div>

        {/* Desktop Navigation */}
        <div className="nav-wrapper desktop-nav">
          <nav className="nav-buttons">
            {navigationConfig.map((item) => {
              if (!shouldShowNavItem(item)) return null;

              if (item.type === 'dropdown') {
                return (
                  <NavDropdown
                    key={item.id}
                    item={item}
                    isActive={activeDropdown === item.id}
                    onHover={() => handleDropdownHover(item.id)}
                    onClick={() => handleDropdownClick(item.id)}
                    onLeave={handleDropdownLeave}
                    currentPath={location.pathname}
                    permissions={permissions}
                  />
                );
              }

              return (
                <NavItem
                  key={item.id}
                  item={item}
                  currentPath={location.pathname}
                />
              );
            })}
            <ThemeToggle />
            {canSeeViewAs() && <ViewAsDropdown />}
            <button className="logout-button" onClick={handleLogout}>
              Log Out
            </button>
          </nav>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle navigation menu"
        >
          <span className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
      </div>

      {/* Mobile Navigation */}
      <MobileMenu
        isOpen={mobileMenuOpen}
        navigationConfig={navigationConfig}
        permissions={permissions}
        currentPath={location.pathname}
        onClose={() => setMobileMenuOpen(false)}
        onLogout={handleLogout}
        showEnvSwitch={false}
        envSwitchUrl=""
        envSwitchLabel=""
      />
    </header>
  );
}

export default Header;